import font from "common/managers/font";
import Color from "common/objects/Color";

// list of styles applied to theme containers, these are high level styles that are generally inherited
// (because custom properties have to be set before they are used in a var ref)
// i.e. font-family inherited from the body
const inheritedStyles = {
    "font-family": "var(--fontFamily)",
    "font-size": "var(--fontSize)",
    "font-style": "var(--fontStyle)",
    "font-weight": "var(--fontWeight)",
    "color": "var(--fontColor)"
}

function prop(name, server, serverFallback) {
    return {name, server, serverFallback};
}

// mapping of css custom property name to server property name
const props = [
    prop("--borderColor", "generalBorderColor"),
    prop("--borderRadius", "generalBorderRadius"),
    prop("--selectionBackgroundColor", "generalSelectionColor"),
    prop("--fontColor", "generalColor"),
    prop("--fontFamily", "generalFontFamily"),
    prop("--fontSize", "generalFontSize"),
    prop("--fontStyle", "generalFontStyle"),
    prop("--lineHeight", "generalLineHeight"),
    prop("--bodyBackgroundColor", "bodyBackgroundColor"),
    prop("--focusColor", "bodyHighlightColor"),
    prop("--buttonBorderColor", "buttonBorderColor", "generalBorderColor"),
    prop("--buttonBorderRadius", "buttonBorderRadius", "generalBorderRadius"),
    prop("--buttonBorderSize", "buttonBorderSize"),
    prop("--buttonBackgroundColor", "buttonBackgroundColor"),
    prop("--buttonBackgroundColorActive", "buttonActiveBackgroundColor"),
    prop("--buttonBackgroundColorHover", "buttonHoverBackgroundColor"),
    prop("--buttonColor", "buttonColor"),
    prop("--buttonColorActive", "buttonActiveColor"),
    prop("--buttonColorHover", "buttonHoverColor"),
    prop("--buttonFontFamily", "buttonFontFamily"),
    prop("--buttonFontSize", "buttonFontSize"),
    prop("--buttonFontWeight", "buttonFontWeight"),
    prop("--linkColor", "linkColor"),
    prop("--linkColorActive", "linkActiveColor"),
    prop("--linkColorHover", "linkHoverColor"),
    prop("--linkFontFamily", "linkFontFamily"),
    prop("--linkFontSize", "linkFontSize"),
    prop("--linkFontStyle", "linkFontStyle"),
    prop("--linkFontStyleActive", "linkActiveFontStyle"),
    prop("--linkFontStyleHover", "linkHoverFontStyle"),
    prop("--linkFontWeight", "linkFontWeight"),
    prop("--linkLineHeight", "lineHeight", "generalLineHeight"),
    prop("--linkBorderColor", "gatewayLinksBorderColor"),
    prop("--linkBorderRadius", "gatewayLinksBorderRadius"),
    prop("--linkBorderSize", "gatewayLinksBorderSize"),
    prop("--linkIconColor", "gatewayLinksIconColor"),
    prop("--linkIconTextSize", "gatewayLinksIconSize", "linkFontSize"),
    prop("--tableColorAltRow", "tableAlternatingRowBackgroundColor"),
    prop("--labelFontStyle", "labelFontStyle"),
    prop("--labelFontWeight", "labelFontWeight"),
    prop("--labelLineHeight", "labelLineHeight", "generalLineHeight"),
    prop("--heading1Color", "heading1Color"),
    prop("--heading1FontFamily", "heading1FontFamily"),
    prop("--heading1FontSize", "heading1FontSize"),
    prop("--heading1FontStyle", "heading1FontStyle"),
    prop("--heading1FontWeight", "heading1FontWeight"),
    prop("--heading1LineHeight", "heading1LineHeight"),
    prop("--heading2Color", "heading2Color"),
    prop("--heading2FontFamily", "heading2FontFamily"),
    prop("--heading2FontSize", "heading2FontSize"),
    prop("--heading2FontStyle", "heading2FontStyle"),
    prop("--heading2FontWeight", "heading2FontWeight"),
    prop("--heading2LineHeight", "heading2LineHeight"),
    prop("--heading3Color", "heading3Color"),
    prop("--heading3FontFamily", "heading3FontFamily"),
    prop("--heading3FontSize", "heading3FontSize"),
    prop("--heading3FontStyle", "heading3FontStyle"),
    prop("--heading3FontWeight", "heading3FontWeight"),
    prop("--heading3LineHeight", "heading3LineHeight"),
    prop("--heading4Color", "heading4Color"),
    prop("--heading4FontFamily", "heading4FontFamily"),
    prop("--heading4FontSize", "heading4FontSize"),
    prop("--heading4FontStyle", "heading4FontStyle"),
    prop("--heading4FontWeight", "heading4FontWeight"),
    prop("--heading4LineHeight", "heading4LineHeight"),
    prop("--headerBorderColor", "headerBarBorderColor"),
    prop("--headerBorderRadius", "headerBarBorderRadius"),
    prop("--headerBorderSize", "headerBarBorderSize"),
    prop("--headerBackgroundColor", "headerBarBackgroundColor"),
    prop("--headerUserColor", "headerBarIconColor"),
    prop("--headerFontFamily", "headerBarGatewayFontFamily"),
    prop("--headerFontSize", "headerBarGatewayFontSize"),
    prop("--headerFontStyle", "headerBarGatewayFontStyle"),
    prop("--headerFontWeight", "headerBarGatewayFontWeight"),
    prop("--headerTitleColor", "headerBarGatewayTitleColor"),
    prop("--headerSubTitleColor", "headerBarPageTitleColor"),
    prop("--sectionBorderColor", "sectionsBorderColor"),
    prop("--sectionBorderRadius", "sectionsBorderRadius"),
    prop("--sectionBorderSize", "sectionsBorderSize"),
    prop("--sectionBackgroundColor", "sectionsBackgroundColor"),
    prop("--contentBorderColor", "searchResultListBorderColor"),
    prop("--contentBorderRadius", "searchResultListBorderRadius"),
    prop("--contentBorderSize", "searchResultListBorderSize"),
    prop("--titleFontSize", "searchPrimaryColumnFontSize"),
    prop("--titleFontWeight", "searchPrimaryColumnFontWeight"),
    prop("--failureBackgroundColor", "gatewayFeedbackFailureBackgroundColor"),
    prop("--failureBorderColor", "gatewayFeedbackFailureBorderColor"),
    prop("--failureBorderRadius", "gatewayFeedbackFailureBorderRadius"),
    prop("--failureBorderSize", "gatewayFeedbackFailureBorderSize"),
    prop("--failureColor", "gatewayFeedbackFailureFontColor"),
    prop("--successBackgroundColor", "gatewayFeedbackSuccessBackgroundColor"),
    prop("--successBorderColor", "gatewayFeedbackSuccessBorderColor"),
    prop("--successBorderRadius", "gatewayFeedbackSuccessBorderRadius"),
    prop("--successBorderSize", "gatewayFeedbackSuccessBorderSize"),
    prop("--successColor", "gatewayFeedbackSuccessFontColor")
]

const ThemeManager = class {
    constructor() {
        this._fonts = [];
    }

    apply(element, theme, elementId = element.getAttribute("id")) {
        if(!elementId) {
            throw new Error("Themed elements must have an id.");
        }
        for(let prop in inheritedStyles) {
            element.style.setProperty(prop, inheritedStyles[prop]);
        }
        this._updateFont(elementId, theme.customFontFamily, theme.customFontSource);
        const hasValue = (value) => value !== null && value !== undefined && value !== "";
        const setProp = function(name, value) {
            if(hasValue(value)) {
                element.style.setProperty(name, value);
            } else {
                element.style.removeProperty(name);
            }
        }
        props.forEach((prop) => {
            setProp(prop.name, theme[prop.server] || theme[prop.serverFallback]);
        });
        let selectionColor;
        if(hasValue(theme.generalSelectionColor)) {
            try {
                selectionColor = theme.generalSelectionColor && Color(theme.generalSelectionColor).isLight() ? "#000" : "#fff";
            } catch(error) {}
        }
        setProp("--selectionColor", selectionColor);
    }

    remove(element, elementId = element.getAttribute("id")) {
        for(let prop in inheritedStyles) {
            element.style.removeProperty(prop);
        }
        this._removeFont(elementId);
        props.forEach((prop) => {
            element.style.removeProperty(prop.name);
        });
        element.style.removeProperty("--selectionColor");
    }

    _fontIndex(id) {
        return this._fonts.findIndex((item) => item.id === id);
    }

    _addFont(id, name, source) {
        if(!name || !source) {
            return;
        }
        font.add(name, source);
        this._fonts.push({
            id,
            font: name
        });
    }

    _updateFont(id, name, source) {
        const themeFontIndex = this._fontIndex(id);
        if(themeFontIndex > -1) {
            const existing = this._fonts[themeFontIndex];
            if(existing.font !== name) {
                this._removeFont(id);
                this._addFont(id, name, source);
            }
        } else {
            this._addFont(id, name, source);
        }
    }

    _removeFont(id) {
        const themeFontIndex = this._fontIndex(id);
        if(themeFontIndex > -1) {
            const removed = this._fonts.splice(themeFontIndex, 1)[0];
            if(this._fonts.length === 0 || this._fonts.every((item) => item.font !== removed.font)) {
                font.remove(removed.font);
            }
        }
    }
}

export default new ThemeManager();
